/**
 * Unified AI Configuration Types
 *
 * Single source of truth for AI provider + model selection, TTS voice mappings,
 * and operation reservation (hold) sizes. NO pricing lives here — USD prices and
 * sell-rates are owned by the accounts price-book (accounts /billing).
 */

import type { VoiceMappingEntry, TtsProvider, CloneProvider } from './voice';
import type { GoogleLLMSettings, ImagenAspectRatio } from './provider';

// ---------------------------------------------------------------------------
// Operation Types — every paid AI operation in the system
// ---------------------------------------------------------------------------

export type OperationType =
  | 'story_pipeline'
  | 'story_regeneration'
  | 'audio_block_generation'
  | 'audio_block_generation_premium'
  | 'page_generation'
  | 'cover_generation'
  | 'character_portrait'
  | 'story_analysis'
  | 'prompt_enhancement'
  | 'block_regeneration'
  | 'story_translation'
  | 'character_enhancement'
  | 'visual_description'
  | 'character_photo_analysis'
  | 'video_generation'
  | 'voice_clone';

export type OperationCategory =
  | 'llm'
  | 'tts_standard'
  | 'tts_premium'
  | 'imagen'
  | 'video'
  | 'free';

export interface OperationConfig {
  reservation: number;
  category: OperationCategory;
  label: string;
}

// ---------------------------------------------------------------------------
// Azure LLM Settings (inlined to avoid circular dependency with provider.ts)
// ---------------------------------------------------------------------------

export interface AzureLLMSettings {
  maxTokens: number;
  temperature: number;
  topP: number;
  frequencyPenalty: number;
  presencePenalty: number;
}

// ---------------------------------------------------------------------------
// Video aspect ratio
// ---------------------------------------------------------------------------

export type VideoAspectRatio = '16:9' | '9:16' | '1:1';

// ---------------------------------------------------------------------------
// Unified AI Configuration
// ---------------------------------------------------------------------------

/**
 * A provider id as the WORKER MANIFEST spells it (`GET /capabilities`).
 *
 * Deliberately not a union: the manifest is the only list of providers and
 * models in the fleet, so adding one must be a worker-side change ONLY. A union
 * here would mean every new provider needs a portal release before the admin can
 * even select it — which is how `xai` ended up wired in the worker, priced in
 * accounts and unreachable from this app. Stored choices are validated against
 * the live manifest on save and reconciled on read (manifestDefaults.ts), so an
 * id the worker does not offer never reaches a job.
 */
export type ManifestProviderId = string;

export interface UnifiedAIConfig {
  id: 'ai_config';
  version: number;

  llm: {
    // Any provider of the manifest's `llm` task. google_vertex_ai is
    // legacy/unimplemented — kept only for back-compat with stored configs and
    // rejected on save (no worker adapter).
    provider: ManifestProviderId;
    /**
     * The chosen model, provider-agnostic. The azure/google sub-objects below
     * hold the same value for the two providers that predate this field (and
     * their tuning settings); a provider without a sub-object — any NEW manifest
     * provider — stores its model here and needs no portal type change.
     */
    model?: string;
    azure?: {
      deploymentName: string;
      apiVersion?: string;
      settings: AzureLLMSettings;
      limits?: { maxPromptTokens: number; maxContextWindow: number };
    };
    google?: {
      model: string;
      location: string;
      settings: GoogleLLMSettings;
    };
    /**
     * STORYBOARD (page-brief) LLM — the chunked `pages-part-*` steps of the book
     * art direction. They are structured extraction (~350 out-tokens per page ×
     * every page) and were the single most expensive step of a book on the main
     * reasoning model ($1.5 for 40 pages on gpt-6-astra). Route them to a cheap
     * fast model; the global `analysis` bible stays on the main LLM.
     * 'inherit' → same provider/model as the main LLM.
     */
    storyboard?: {
      /** 'inherit' or any provider of the manifest's `llm` task. */
      provider: 'inherit' | ManifestProviderId;
      model?: string;
    };
  };

  tts: {
    /** Default TTS engine for preset voices; a voice mapping may pin its own. */
    provider: TtsProvider;
    /** Engine new voice clones are created on (family clones route to their own engine). */
    cloneProvider?: CloneProvider;
    /** TTS model for cloned voices on that engine (empty = engine default; dev: 's2.1-pro-free'). */
    cloneModel?: string;
    standardModel: string;
    premiumModel: string;
    settings: { stability: number; similarityBoost: number };
    voiceMappings: VoiceMappingEntry[];
  };

  imagen: {
    /** Active image engine (a provider of the manifest's `image` task). */
    provider: ManifestProviderId;
    model: string;
    /** Azure image api-version (only used when provider==='azure_openai'). */
    apiVersion?: string;
    aspectRatio: ImagenAspectRatio;
    /**
     * Render quality per image op — a level the manifest lists for the chosen
     * model (`models[].quality`, e.g. low/medium/high); absent = the portal's
     * cost default ('low'). Ignored by models without a quality knob.
     */
    quality?: { pages?: string; cover?: string; portraits?: string };
    /**
     * CHARACTER PORTRAITS on a different engine than the pages. Unset = the
     * engine above.
     *
     * It applies to portraits drawn FROM A DESCRIPTION. A portrait drawn from a
     * parent's PHOTO ignores it unless the engine can take photos at all
     * (Gemini today): the photo has to be accepted as an identity reference and
     * survive the provider's output moderation, and every other provider
     * refuses a child drawn from one. See getPortraitSelection.
     *
     * The split exists because the two jobs have different constraints: pages
     * need the cheapest model that holds the book's look, while the invented
     * cast can be drawn by whichever engine gives them the most character.
     */
    portraits?: { provider: ManifestProviderId; model: string };
    /**
     * WARDROBE SHEET (illustrations/services/wardrobeRefs.ts): a character with 2+ outfit variants
     * in a book gets ONE edit of its canonical reference with every outfit side by side, cut into
     * the variant references — instead of one edit per variant. OFF unless true; the
     * WARDROBE_SHEET env (1/0) overrides it.
     */
    wardrobeSheet?: boolean;
  };

  video: {
    provider: ManifestProviderId;
    model: string;
    duration: number;
    aspectRatio: VideoAspectRatio;
  };

  // Transcription (speech-to-text): the provider+model the admin selects to turn a
  // recorded voice note (any language) into text the pipeline reuses. 'azure_openai' =
  // gpt-transcribe (the fleet default since 2026-09-26), 'openai' = Whisper (removed from
  // the OpenAI API 2027-02-26); 'fake' = zero-spend dev stand.
  // Optional for back-compat with stored configs written before this field existed
  // (getConfig back-fills the default). NOTE: this is the SELECTION config only —
  // the admin record-UI tab (like the LLM/TTS/Imagen/Video tabs) is a follow-up.
  transcription?: {
    provider: ManifestProviderId;
    model: string;
    /** Optional ISO-639-1 language hint stamped into the job; empty = auto-detect. */
    language?: string;
  };

  // Vision (image understanding): the page critic (visual QA of rendered book
  // pages) and photo analysis. 'fake' = zero-spend dev stand (canned verdicts).
  // Optional/back-compat like transcription (getConfig back-fills the default).
  vision?: {
    provider: ManifestProviderId;
    model: string;
    /** Page critic on/off (visual QA of every rendered page + corrective re-render,
     *  billed to the user) and the reference-sheet crop compare. OFF unless set to true (owner
     *  2026-10-31: no silent quality loop); PAGE_QA_ENABLED=0 env is the kill switch. */
    pageQaEnabled?: boolean;
  };

  // Realtime voice agent (story-creation v2). Optional/back-compat like
  // transcription. `model` is the realtime model/deployment; `transcriptionModel`
  // transcribes the CHILD's speech within the session.
  realtime?: {
    /** LEGACY SPELLING (not a manifest id): 'google' ↔ the manifest's
     *  google_ai_studio, 'azure' ↔ azure_openai — mapped by
     *  manifestDefaults.REALTIME_TO_MANIFEST. */
    provider: 'openai' | 'azure' | 'google' | 'xai' | 'fake';
    model: string;
    transcriptionModel?: string;
  };

  operations: Record<OperationType, OperationConfig>;

  /**
   * One-time ONBOARDING gift for a brand-new account (per-app allowance lot,
   * source 'onboarding_bonus', idempotent on periodRef='onboarding'): `credits`
   * of portal allowance that expire after `expiresInDays`. Admin-editable here
   * (Economy tab).
   *
   * The gift has to cover the whole thing we promise for free, or the promise
   * breaks halfway: at 1500 a campaign book stopped at three pages of ten, with
   * seven characters never drawn, and the parent saw a present with holes in
   * it.
   *
   * MEASURED, on the finished campaign book of 2026-10-23: ten pages 2 038,
   * eleven references 950, the cover 190, the story 70, its analysis 53 — 3 351
   * tokens, which is $3.35 of our own cost, because a token is pegged to a
   * milli-dollar of it. 4 500 covers that with a third to spare for a redrawn
   * page or a bigger cast.
   *
   * It was 7 500, which is $7.50 of cost and about €14 at what a top-up pack
   * sells for — two and a bit books, given away with every sign-up, for a
   * promise of one.
   */
  onboarding?: {
    credits: number;
    expiresInDays: number;
  };

  updatedAt: string;
  updatedBy: string;
}

/** Code default for the onboarding gift — used until an admin saves the row. */
export const DEFAULT_ONBOARDING = { credits: 4500, expiresInDays: 14 } as const;

// ---------------------------------------------------------------------------
// Default operations registry
// ---------------------------------------------------------------------------

// Reservation = the per-op HOLD placed before the job runs; settle later charges
// the price-book actual and refunds the rest.
//
// SIZED FROM MEASUREMENT, not from a guess (owner decision 2026-10-20: a hold
// must be large enough that we never carry the risk). Each number is the ninth
// decile of what that operation ACTUALLY settled on prod — allowance_holds,
// 247 settled charges — rounded up:
//
//   page_generation    p90 106, p99 189, worst 387  → 190
//   cover_generation   p90 108, p99 138, worst 141  → 150
//   character_portrait p90  64, p99  95, worst 106  → 110
//   story_pipeline     p90 371, p99 461, worst 471  → 500
//   story_analysis     p90 796, p99 819, worst 821  → 850
//   audio_block        p90  14, p99  24, worst  26  →  30
//
// The old numbers were the opposite of safe: a page held 47 against a 106
// decile, and every single character portrait overran its 9. Where there is no
// production sample yet (translation, video, enhancements) the number is the
// nearest measured relative — an LLM pass the size of a story, a video second
// at the manifest's rate — rather than a round number nobody checked.
//
// A bigger hold is refunded down to the real charge on settle, so the cost of
// being generous here is a start blocked for a family whose pool is nearly
// empty — not money. That is the trade the owner asked for.
export const DEFAULT_OPERATIONS: Record<OperationType, OperationConfig> = {
  story_pipeline:           { reservation: 500, category: 'llm',          label: 'Story generation' },
  story_regeneration:       { reservation: 500, category: 'llm',          label: 'Story regeneration' },
  audio_block_generation:           { reservation: 30,  category: 'tts_standard', label: 'Block audio (standard)' },
  audio_block_generation_premium:  { reservation: 60,  category: 'tts_premium',  label: 'Block audio (premium)' },
  page_generation:          { reservation: 190, category: 'imagen',       label: 'Page illustration' },
  // 150 was under the measured 190: a cover is drawn larger than a page, and
  // it overran its hold on every book.
  cover_generation:         { reservation: 200, category: 'imagen',       label: 'Book cover' },
  character_portrait:       { reservation: 110, category: 'imagen',       label: 'Character portrait' },
  story_analysis:           { reservation: 850, category: 'llm',          label: 'Story analysis' },
  prompt_enhancement:       { reservation: 20,  category: 'llm',          label: 'Prompt enhancement' },
  block_regeneration:       { reservation: 60,  category: 'llm',          label: 'Block regeneration' },
  story_translation:        { reservation: 500, category: 'llm',          label: 'Story translation' },
  character_enhancement:    { reservation: 30,  category: 'llm',          label: 'Character enhancement' },
  visual_description:       { reservation: 20,  category: 'llm',          label: 'Visual description' },
  character_photo_analysis: { reservation: 30,  category: 'llm',          label: 'Photo analysis' },
  video_generation:         { reservation: 300, category: 'video',        label: 'Video generation' },
  // One-off per voice: MiniMax bills $1.50 per clone (≈221 tokens at the price
  // book); ElevenLabs clones price to 0 and the hold is released on settle.
  voice_clone:              { reservation: 230, category: 'tts_standard', label: 'Voice clone' },
};
